import { useEffect, useRef, useState } from 'react';
import { useRefCallback } from 'hooks';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { delayAsync } from 'utils/delay';
import { patchPromotions, PromotionPatchType } from '../actions';
import { INTRO_GIFT_PROMOTION_ID } from '../introGiftPromotion';
import { selectFeedPromotion, selectPromotionForPlatform } from '../selectors';
import useDismissOfferModal from './useDismissOfferModal';
export const usePromoDiscountBanner = () => {
    const { dismissOfferModal } = useDismissOfferModal();
    // The ref is to prevent rendering another promo card when the current one has been dismissed
    const currentPromotionIdRef = useRef(undefined);
    const promotion = useAppSelector((state) => selectFeedPromotion(state, currentPromotionIdRef.current));
    currentPromotionIdRef.current = promotion?.id;
    const promotionForPlatform = useAppSelector((store) => promotion ? selectPromotionForPlatform(store, promotion.id) : null);
    const dispatch = useAppDispatch();
    const [isDismissed, setDismissed] = useState(!!promotion?.feedCardDismissedAt);
    useEffect(() => {
        setDismissed(!!promotion?.feedCardDismissedAt);
    }, [promotion?.id, promotion?.feedCardDismissedAt]);
    const navigation = useMainStackNavigation();
    const onOpen = useRefCallback(() => {
        if (!promotion) {
            return;
        }
        navigation.navigateDeprecated('PromoDiscountScreen', {
            promotionId: promotion.id,
        });
    });
    const onOpenWithModalSkip = useRefCallback(() => {
        if (!promotion) {
            return;
        }
        navigation.navigateDeprecated('PromoDiscountScreen', {
            promotionId: promotion.id,
            skipDismissModal: true,
        });
    });
    const onDismiss = useRefCallback(() => {
        const { productName, formattedPrices } = promotionForPlatform || {};
        const { percentOff, savingPerYear } = formattedPrices || {};
        const onDismissOffer = async () => {
            if (!promotion) {
                return;
            }
            setDismissed(true);
            // Wait for the local state animation to finish before unmount
            await delayAsync(300);
            // The synthetic intro-gift promo has no backend row, so persist the
            // dismissal locally instead of PATCHing. The offer stays "active"
            // (still returned by the selector) until the 24h window expires.
            if (promotion.id === INTRO_GIFT_PROMOTION_ID) {
                dispatch(setDeviceSettingsKey('introGiftFeedDismissedAt', Date.now()));
                return;
            }
            dispatch(patchPromotions({
                promotionId: promotion.id,
                patchType: PromotionPatchType.FEED_CARD_DISMISSED,
            }));
        };
        if (productName && percentOff && savingPerYear) {
            dismissOfferModal(onOpenWithModalSkip, onDismissOffer, productName, percentOff, savingPerYear);
        }
        else {
            onDismissOffer();
        }
    });
    if (promotion?.acceptedAt || !promotionForPlatform) {
        return null;
    }
    return {
        ...promotionForPlatform,
        isDismissed,
        onOpen,
        onDismiss,
        feedCardDismissedAt: promotion?.feedCardDismissedAt,
    };
};
